import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { ROUTES } from "../../../routes/routes.constants";
import CommonTable from "../../../common_components/CommonTable";
import Pagination from "../../../common_components/Pagination";
import TableToolbar from "../../../common_components/TableToolbar";
import { ExportButton } from "../../../common_components/FormInputs";
import { SUMMARY_ROW_POSITIONS, TABLE_TYPES, EXPAND_TRIGGERS } from "../../../common_components/tableConstants";
import DiffChip from "./DiffChip";
import AmBadges from "./AmBadges";
import { commissionTableContainerSx, CurrencyText } from "./commissionTableShared";
const ITEMS_PER_PAGE = 10;
/** "YYYY-MM-DD" → "MM/DD/YYYY" (parsed by parts to avoid any timezone shift). */
const formatYmd = (ymd) => {
    if (!ymd)
        return "";
    const [y, m, d] = ymd.split("-");
    if (!y || !m || !d)
        return "";
    return `${m}/${d}/${y}`;
};
// ── Grouped columns (mirrors the Account Performance table structure) ──
const columns = [
    { id: "client", label: "Client", width: "196px", align: "left", sortable: true },
    { id: "am", label: "AM", width: "89px", align: "center", sortable: false },
    {
        id: "commissionGroup",
        label: "Commission",
        align: "center",
        children: [
            { id: "comm_expected", label: "Expected Day's", width: "152px", align: "left", sortable: true },
            { id: "comm_days", label: "Day's Commission", width: "143px", align: "left", sortable: true },
            { id: "comm_diff", label: "Diff", width: "104px", align: "left", sortable: true },
            { id: "comm_avg", label: "Average Daily", width: "143px", align: "left", sortable: true },
        ],
    },
    {
        id: "revenueGroup",
        label: "Revenue",
        align: "center",
        children: [
            { id: "rev_expected", label: "Expected Day's", width: "145px", align: "left", sortable: true },
            { id: "rev_days", label: "Day's Revenue", width: "145px", align: "left", sortable: true },
            { id: "rev_diff", label: "Diff", width: "110px", align: "left", sortable: true },
            { id: "rev_avg", label: "Average Daily", width: "143px", align: "left", sortable: true },
        ],
    },
];
// Leaf columns the user is allowed to hide (Client stays pinned/visible).
const HIDEABLE_COLUMN_IDS = [
    "am",
    "comm_expected", "comm_days", "comm_diff", "comm_avg",
    "rev_expected", "rev_days", "rev_diff", "rev_avg",
];
// Maps a column id to the raw sortable value on a row.
const SORT_ACCESSORS = {
    client: (r) => r.client.toLowerCase(),
    comm_expected: (r) => r.commission.expected,
    comm_days: (r) => r.commission.days,
    comm_diff: (r) => r.commission.diff.percentage,
    comm_avg: (r) => r.commission.avgDaily,
    rev_expected: (r) => r.revenue.expected,
    rev_days: (r) => r.revenue.days,
    rev_diff: (r) => r.revenue.diff.percentage,
    rev_avg: (r) => r.revenue.avgDaily,
};
const DailyUpdateTab = ({ rows, totals, onExport, exporting, canOpenReport = true, asOfDate, primaryMonth }) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState("comm_expected");
    const [sortOrder, setSortOrder] = useState("desc");
    const [search, setSearch] = useState("");
    // ── Column visibility ─────────────────────────────────────
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchorEl, setColumnVisibilityAnchorEl] = useState(null);
    // Client-name search (table header search box).
    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q)
            return rows;
        return rows.filter((r) => r.client.toLowerCase().includes(q));
    }, [rows, search]);
    // Reset to the first page whenever the visible set changes (search).
    const filteredCount = filteredRows.length;
    React.useEffect(() => {
        setPage(1);
    }, [filteredCount]);
    const sortedRows = useMemo(() => {
        const accessor = SORT_ACCESSORS[sortBy];
        if (!accessor)
            return filteredRows;
        return [...filteredRows].sort((a, b) => {
            const va = accessor(a);
            const vb = accessor(b);
            if (va < vb)
                return sortOrder === "asc" ? -1 : 1;
            if (va > vb)
                return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredRows, sortBy, sortOrder]);
    const totalPages = Math.max(1, Math.ceil(sortedRows.length / ITEMS_PER_PAGE));
    const pagedRows = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return sortedRows.slice(start, start + ITEMS_PER_PAGE);
    }, [sortedRows, page]);
    // ── Body cells ────────────────────────────────────────────
    const renderCell = (row, col) => {
        switch (col.id) {
            case "client":
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, px: 1, cursor: canOpenReport ? "pointer" : "default" }, onClick: canOpenReport ? () => {
                        sessionStorage.setItem("commission_tracker_client_id", row.id);
                        if (primaryMonth)
                            sessionStorage.setItem("commission_tracker_month", primaryMonth);
                        navigate(ROUTES.commissionTrackerReport.path, { state: { storeId: row.id, storeName: row.client, month: primaryMonth } });
                    } : undefined, children: [_jsx(Typography, { sx: { fontWeight: 400, color: "#27272a", fontSize: "14px !important", whiteSpace: "nowrap" }, children: row.client }), canOpenReport && row.clientLinked && _jsx("img", { src: "/assets/link.svg", alt: "link" })] }));
            case "am":
                return _jsx(Box, { sx: { px: 1 }, children: _jsx(AmBadges, { managers: row.am }) });
            case "comm_expected": return row.commission.expected !== 0 ? _jsx(CurrencyText, { value: row.commission.expected }) : "-";
            case "comm_days": return row.commission.days !== 0 ? _jsx(CurrencyText, { value: row.commission.days }) : "-";
            case "comm_diff": return row.commission.diff.percentage !== 0 ? _jsx(Box, { sx: { px: 1 }, children: _jsx(DiffChip, { percentage: row.commission.diff.percentage, trend: row.commission.diff.trend }) }) : "-";
            case "comm_avg": return row.commission.avgDaily !== 0 ? _jsx(CurrencyText, { value: row.commission.avgDaily }) : "-";
            case "rev_expected": return row.revenue.expected !== 0 ? _jsx(CurrencyText, { value: row.revenue.expected }) : "-";
            case "rev_days": return row.revenue.days !== 0 ? _jsx(CurrencyText, { value: row.revenue.days }) : "-";
            case "rev_diff": return row.revenue.diff.percentage !== 0 ? _jsx(Box, { sx: { px: 1 }, children: _jsx(DiffChip, { percentage: row.revenue.diff.percentage, trend: row.revenue.diff.trend }) }) : "-";
            case "rev_avg": return row.revenue.avgDaily !== 0 ? _jsx(CurrencyText, { value: row.revenue.avgDaily }) : "-";
            default: return null;
        }
    };
    // ── Summary (footer) row ──────────────────────────────────
    const renderSummaryRow = (_rows, col) => {
        const c = totals.commission;
        const r = totals.revenue;
        switch (col.id) {
            case "client":
                return _jsx(Box, { sx: { px: 1 }, children: _jsx(Typography, { sx: { fontWeight: 700, color: "#181d27", fontSize: "14px !important" }, children: "Total" }) });
            case "am":
                return "";
            case "comm_expected": return _jsx(CurrencyText, { value: c.expected, bold: true });
            case "comm_days": return _jsx(CurrencyText, { value: c.days, bold: true });
            case "comm_diff": return _jsx(Box, { sx: { px: 1 }, children: _jsx(DiffChip, { percentage: c.diff.percentage, trend: c.diff.trend, strong: true }) });
            case "comm_avg": return _jsx(CurrencyText, { value: c.avgDaily, bold: true });
            case "rev_expected": return _jsx(CurrencyText, { value: r.expected, bold: true });
            case "rev_days": return _jsx(CurrencyText, { value: r.days, bold: true });
            case "rev_diff": return _jsx(Box, { sx: { px: 1 }, children: _jsx(DiffChip, { percentage: r.diff.percentage, trend: r.diff.trend, strong: true }) });
            case "rev_avg": return _jsx(CurrencyText, { value: r.avgDaily, bold: true });
            default: return "";
        }
    };
    const showSummaryRow = sortedRows.length > 0;
    const currentMonth = new Date().toISOString().slice(0, 7);
    const isCurrentMonth = primaryMonth === currentMonth;
    return (_jsxs(Box, { sx: {
            bgcolor: "#fff",
            border: "1px solid #EAECF0",
            borderRadius: "8px",
            overflow: "hidden",
        }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    justifyContent: isCurrentMonth ? "space-between" : "flex-end",
                    alignItems: "center",
                    pl: 2,
                    borderBottom: "1px solid #EAECF0",
                    bgcolor: "#fff",
                }, children: [isCurrentMonth && (_jsx("span", { style: { fontSize: '16px', marginTop: "8px", fontWeight: "600" }, children: formatYmd(asOfDate) })), _jsx(TableToolbar, { title: "", showSearch: true, searchValue: search, onSearchChange: setSearch, searchPlaceholder: "Search client...", showRows: true, enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColumnVisibilityAnchorEl(e.currentTarget), showFullScreen: true, customActions: rows.length > 0 ? (_jsx(ExportButton, { onClick: () => onExport?.(), loading: exporting })) : null })] }), _jsx(Box, { className: "table-container", sx: { ...commissionTableContainerSx, position: "relative", borderRadius: 0, borderLeft: "none", borderRight: "none", borderBottom: "none" }, children: _jsx(CommonTable, { columns: columns, rows: sortedRows, renderCell: renderCell, sortConfig: { columnId: sortBy, direction: sortOrder }, onSortChange: (id, dir) => { setSortBy(id); setSortOrder(dir || (sortOrder === "asc" ? "desc" : "asc")); }, expandTrigger: EXPAND_TRIGGERS.NONE, enableColumnActions: false, enableColumnReorder: false, enableColumnPinning: false, enableColumnFilters: false, enableColumnVisibility: false, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchorEl: columnVisibilityAnchorEl, onColumnVisibilityClose: () => setColumnVisibilityAnchorEl(null), columnVisibilityConfig: { enable: true, columns: HIDEABLE_COLUMN_IDS, enableReorder: false }, enableSelection: false, enableSummaryRow: showSummaryRow, renderSummaryRow: showSummaryRow ? renderSummaryRow : undefined, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM, type: TABLE_TYPES.GROUPED, isLoading: false, stickyHeader: true, maxHeight: "calc(100vh - 200px)", emptyMessage: "No commission data available." }) }), _jsx(Box, { className: "user-list-pagination-wrapper", children: _jsx(Pagination
                // currentPage={page}
                // totalPages={totalPages}
                , { 
                    // currentPage={page}
                    // totalPages={totalPages}
                    currentPage: 1, totalPages: 1, onPageChange: setPage, maxVisiblePages: 5, totalEntries: sortedRows.length, itemsPerPage: ITEMS_PER_PAGE }) })] }));
};
export default DailyUpdateTab;
